import React, { useState } from 'react';
import { parseISO, format as fnsFormat, isValid, startOfMonth } from 'date-fns';
import { useField, useFormikContext } from 'formik';

import {
    Autocomplete,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { InfoOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import type { MuiTelInputCountry } from 'mui-tel-input';

import PhoneNumberField from '../PhoneNumberField/PhoneNumberField';
import AiAssistField from './aiAssistField';
import { useFormBusy } from './formBusyContext';
import TemplateInput from './templateInput';
import InfoTooltip from 'components/v2/common/infoTooltip';

import useTranslate from 'hooks/useTranslate';
import type { AiRecommendationKind, CustomOptionInterface } from 'types';

export type FormFieldType =
    | 'string'
    | 'template'
    | 'number'
    | 'select'
    | 'radio'
    | 'email'
    | 'password'
    | 'multiline'
    | 'autosuggest'
    | 'aiselect'
    | 'date'
    | 'datetime'
    | 'monthyear'
    | 'phone'
    | 'checkbox'
    | 'time';

export interface FormFieldProps {
    name: string;
    label: string;
    type?: FormFieldType;
    options?: CustomOptionInterface<string | number | boolean | null | undefined>[];
    required?: boolean;
    multiline?: boolean;
    minRows?: number;
    defaultCountry?: MuiTelInputCountry;
    maxLength?: number;
    size?: 'small' | 'medium';
    disabled?: boolean;
    error?: boolean;
    /** Allow more than one value to be selected by an autosuggest field. */
    multiple?: boolean;
    /** Secondary caption shown under a checkbox label (permission-style). */
    description?: string;
    /** Optional translated help text shown beside a radio label or at the end of a text or single autosuggest field. */
    tooltip?: string;
    /** Catalogue for type="aiselect": occupation titles or fields of study. */
    kind?: AiRecommendationKind;
    /** Market code for type="aiselect" (AU, CA, or UK). */
    countryCode?: string;
    /** May return Formik's setValues promise (void | FormikErrors). */
    onChange?: (value: unknown) => void | Promise<unknown>;
    /**
     * When false, `label` is shown as written.
     * Translated labels are i18n keys, and i18next splits them on ".".
     */
    translateLabel?: boolean;
    // Extra passthrough props for MUI / date constraints, etc.
    minDate?: unknown;
    maxDate?: unknown;
    maxDaysFromToday?: boolean;
    minDaysFromToday?: boolean;
    [key: string]: unknown;
}

const FormField: React.FC<FormFieldProps> = ({
    name,
    label,
    type = 'string',
    options = [],
    required = false,
    multiline = false,
    minRows,
    defaultCountry,
    maxLength,
    size = 'small',
    onChange,
    minDate,
    maxDate,
    maxDaysFromToday,
    minDaysFromToday,
    disabled: disabledProp,
    error: errorProp,
    multiple = false,
    description,
    tooltip,
    kind,
    countryCode,
    translateLabel = true,
    ...props
}) => {
    const { translate } = useTranslate();
    const formBusy = useFormBusy();
    const isDisabled = Boolean(disabledProp) || formBusy;
    const [showPassword, setShowPassword] = useState(false);
    const [field, meta] = useField(name);
    const { setFieldValue, setFieldTouched, submitCount } = useFormikContext<unknown>();

    // By default, show errors after touch or submit; callers can override this per field.
    const isError = errorProp ?? Boolean(meta.error && (meta.touched || submitCount > 0));
    const helperText = isError && meta.error ? String(meta.error) : ' ';

    /**
     * Update value and re-validate so helper text clears as the user fixes the field.
     *
     * When a parent `onChange` is provided it often updates related fields too
     * (e.g. clearing `job_offers` when "has job offer" becomes No). In that case we
     * must not validate until after the parent handler runs — otherwise Yup.when()
     * can see an empty array while the flag is still true and report min(1) errors.
     */
    const handleChange = (fieldName: string, value: unknown) => {
        if (onChange) {
            // Parent may update related fields (e.g. clear job_offers when selecting No).
            // Wait for parent updates before validating so Yup.when() sees consistent state.
            void setFieldValue(fieldName, value, false)
                .then(() => onChange(value))
                .then(() => setFieldTouched(fieldName, true, true));
            return;
        }

        void setFieldValue(fieldName, value, true).then(() => {
            void setFieldTouched(fieldName, true, false);
        });
    };

    /** Blur handler — validate the current value when leaving the field. */
    const markTouched = () => {
        void setFieldTouched(name, true, true);
    };

    const toDate = (val: unknown): Date | null => {
        if (!val) return null;
        if (val instanceof Date) return isValid(val) ? val : null;
        // moment / dayjs-like objects
        if (typeof val === 'object' && val !== null && 'toDate' in val) {
            try {
                const d = (val as { toDate: () => Date }).toDate();
                return isValid(d) ? d : null;
            } catch {
                return null;
            }
        }
        if (typeof val === 'string') {
            // Support both ISO and yyyy-MM-dd
            const d = parseISO(val.length === 10 ? `${val}T00:00:00` : val);
            return isValid(d) ? d : null;
        }
        return null;
    };

    const resolvedMaxDate: Date | undefined = maxDaysFromToday
        ? new Date()
        : (toDate(maxDate) ?? undefined);
    const resolvedMinDate: Date | undefined = minDaysFromToday
        ? new Date()
        : (toDate(minDate) ?? undefined);

    const displayLabel = translateLabel ? translate(label) : label;
    const labelWithRequired = displayLabel && required ? `${displayLabel} *` : displayLabel;
    const fieldLabel = displayLabel ? labelWithRequired : undefined;

    // RADIO
    if (type === 'radio') {
        return (
            <FormControl
                component="fieldset"
                sx={{ width: '100%', mt: 0.5, mb: 1 }}
                error={isError}
            >
                {displayLabel ? (
                    <FormLabel>
                        {displayLabel}
                        {tooltip && <InfoTooltip title={translate(tooltip)} color="#686868" />}
                    </FormLabel>
                ) : null}

                <RadioGroup
                    row
                    name={name}
                    value={String(field.value ?? '')}
                    onChange={(e) => {
                        const selected = options.find(
                            (opt) => String(opt.value) === e.target.value
                        );
                        const finalValue = selected ? selected.value : e.target.value;
                        handleChange(name, finalValue);
                    }}
                >
                    {options.map((opt, index) => (
                        <FormControlLabel
                            key={index}
                            value={String(opt.value)}
                            control={
                                <Radio
                                    disableRipple
                                    size={size}
                                    color={isError ? 'error' : 'primary'}
                                    sx={isError ? { color: 'error.main' } : undefined}
                                    slotProps={{ input: { 'aria-invalid': isError } }}
                                />
                            }
                            label={opt.label}
                            disabled={isDisabled}
                        />
                    ))}
                </RadioGroup>

                <FormHelperText sx={{ mx: 0 }}>{helperText}</FormHelperText>
            </FormControl>
        );
    }

    // AI SELECT — same Formik autosuggest, with the recommendation assistant inside the field
    if (type === 'aiselect') {
        return (
            <AiAssistField
                label={labelWithRequired}
                kind={kind ?? 'occupation'}
                countryCode={countryCode ?? ''}
                options={options}
                value={field.value}
                disabled={isDisabled}
                error={isError}
                helperText={helperText}
                size={size}
                onBlur={markTouched}
                onChange={(next) => handleChange(name, next)}
            />
        );
    }

    // AUTOSUGGEST
    if (type === 'autosuggest') {
        if (multiple) {
            const selectedValues = Array.isArray(field.value) ? field.value : [];

            return (
                <Autocomplete
                    multiple
                    fullWidth
                    size={size}
                    disabled={isDisabled}
                    value={options.filter((option) => selectedValues.includes(option.value))}
                    options={options}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    onChange={(_, value) => {
                        handleChange(
                            name,
                            value.map((option) => option.value)
                        );
                    }}
                    onBlur={markTouched}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size={size}
                            label={labelWithRequired}
                            error={isError}
                            helperText={helperText}
                            onBlur={markTouched}
                        />
                    )}
                />
            );
        }

        return (
            <Autocomplete
                fullWidth
                size={size}
                disabled={isDisabled}
                value={options.find((opt) => opt.value === field.value) || null}
                options={options}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                onChange={(_, value) => {
                    handleChange(
                        name,
                        value ? (typeof value === 'string' ? value : value.value) : ''
                    );
                }}
                onBlur={markTouched}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size={size}
                        label={fieldLabel}
                        placeholder={
                            typeof props.placeholder === 'string' ? props.placeholder : undefined
                        }
                        error={isError}
                        helperText={helperText}
                        onBlur={markTouched}
                        slotProps={
                            tooltip
                                ? {
                                      ...params.slotProps,
                                      input: {
                                          ...params.slotProps.input,
                                          endAdornment: (
                                              <>
                                                  <InfoTooltip
                                                      title={translate(tooltip)}
                                                      color="#686868"
                                                      size={20}
                                                  />
                                                  {params.slotProps.input.endAdornment}
                                              </>
                                          ),
                                      },
                                  }
                                : params.slotProps
                        }
                    />
                )}
            />
        );
    }

    // CHECKBOX
    if (type === 'checkbox') {
        const descriptionText = description ? translate(description) : undefined;

        return (
            <FormControl error={isError} disabled={isDisabled} sx={{ width: '100%' }}>
                <FormControlLabel
                    sx={
                        descriptionText
                            ? {
                                  m: 0,
                                  px: 1,
                                  py: 0.75,
                                  borderRadius: 1,
                                  alignItems: 'flex-start',
                                  '&:hover': { bgcolor: 'action.hover' },
                                  '& .MuiCheckbox-root': { pt: 0.25 },
                              }
                            : undefined
                    }
                    control={
                        <Checkbox
                            size={size}
                            checked={Boolean(field.value)}
                            onChange={(e) => {
                                handleChange(name, e.target.checked);
                            }}
                            name={name}
                            disabled={isDisabled}
                        />
                    }
                    label={
                        descriptionText ? (
                            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, lineHeight: 1.4 }}
                                >
                                    {labelWithRequired}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.4, display: 'block' }}
                                >
                                    {descriptionText}
                                </Typography>
                            </Stack>
                        ) : (
                            labelWithRequired
                        )
                    }
                />

                {descriptionText && !isError ? null : (
                    <FormHelperText sx={{ mx: 0 }}>{helperText}</FormHelperText>
                )}
            </FormControl>
        );
    }

    // DATE / MONTHYEAR / DATETIME
    // Error helperText is rendered with FormHelperText outside the picker
    // (same pattern as FormikMonthYearPicker — more reliable than slotProps alone).
    if (type === 'date' || type === 'monthyear' || type === 'datetime') {
        const commonSlotProps = {
            field: { clearable: true },
            textField: {
                size,
                fullWidth: true as const,
                name,
                error: isError,
                onBlur: markTouched,
            },
        };

        return (
            <FormControl fullWidth error={isError}>
                {type === 'datetime' ? (
                    <DateTimePicker
                        label={fieldLabel}
                        value={toDate(field.value)}
                        disabled={isDisabled}
                        minDate={resolvedMinDate}
                        maxDate={resolvedMaxDate}
                        onChange={(val: Date | null) => {
                            handleChange(name, val && isValid(val) ? val.toISOString() : '');
                        }}
                        slotProps={commonSlotProps}
                    />
                ) : (
                    <DatePicker
                        views={type === 'monthyear' ? ['year', 'month'] : undefined}
                        openTo={type === 'monthyear' ? 'year' : undefined}
                        format={type === 'monthyear' ? 'MM/yyyy' : 'dd/MM/yyyy'}
                        label={fieldLabel}
                        value={toDate(field.value)}
                        disabled={isDisabled}
                        minDate={resolvedMinDate}
                        maxDate={resolvedMaxDate}
                        onChange={(val: Date | null) => {
                            if (type === 'monthyear') {
                                if (val && isValid(val)) {
                                    handleChange(name, fnsFormat(startOfMonth(val), 'yyyy-MM-dd'));
                                } else {
                                    handleChange(name, '');
                                }
                            } else {
                                const formatted =
                                    val && isValid(val) ? fnsFormat(val, 'yyyy-MM-dd') : '';
                                handleChange(name, formatted);
                            }
                        }}
                        slotProps={commonSlotProps}
                    />
                )}
                <FormHelperText sx={{ mx: 1.75, mt: 0.25 }}>{helperText}</FormHelperText>
            </FormControl>
        );
    }

    if (type === 'time') {
        return (
            <TextField
                fullWidth
                label={fieldLabel}
                error={isError}
                helperText={helperText}
                type="time"
                size={size}
                name={name}
                {...props}
                disabled={isDisabled}
                value={field.value ?? ''}
                onChange={(e) => handleChange(name, e.target.value)}
                onBlur={markTouched}
            />
        );
    }

    // PHONE
    if (type === 'phone') {
        return (
            <PhoneNumberField
                {...props}
                size={size}
                fullWidth
                name={name}
                label={fieldLabel}
                value={(field.value as string) || ''}
                onChange={(value) => {
                    handleChange(name, value);
                }}
                onBlur={markTouched}
                defaultCountry={defaultCountry}
                error={isError}
                helperText={helperText}
                disabled={isDisabled}
            />
        );
    }

    // PASSWORD
    if (type === 'password') {
        return (
            <TextField
                fullWidth
                size={size}
                label={fieldLabel}
                error={isError}
                helperText={helperText}
                type={showPassword ? 'text' : 'password'}
                name={name}
                {...props}
                disabled={isDisabled}
                value={field.value ?? ''}
                onChange={(e) => handleChange(name, e.target.value)}
                onBlur={markTouched}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    disabled={isDisabled}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
        );
    }

    if (type === 'template') {
        return (
            <TemplateInput
                label={labelWithRequired}
                error={isError}
                helperText={helperText}
                disabled={isDisabled}
                maxLength={props.maxLength as number | undefined}
                value={field.value}
                onChange={(value) => handleChange(name, value)}
            />
        );
    }

    // DEFAULT TEXT / NUMBER / EMAIL / SELECT
    // Route through handleChange so errors clear as soon as the value becomes valid
    // (avoids relying on Formik's field.onChange timing with custom wrappers).
    const isSelect = type === 'select';
    return (
        <TextField
            fullWidth
            select={isSelect || undefined}
            label={fieldLabel}
            error={isError}
            helperText={helperText}
            type={
                isSelect
                    ? undefined
                    : type === 'number'
                      ? 'number'
                      : type === 'email'
                        ? 'email'
                        : 'text'
            }
            multiline={multiline}
            minRows={multiline ? minRows || 3 : undefined}
            size={size}
            name={name}
            {...props}
            disabled={isDisabled}
            value={field.value ?? ''}
            onChange={(e) => handleChange(name, e.target.value)}
            onBlur={markTouched}
            slotProps={
                !isSelect && (tooltip || maxLength)
                    ? {
                          ...(maxLength ? { htmlInput: { maxLength } } : {}),
                          ...(tooltip
                              ? {
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip
                                                    title={translate(tooltip)}
                                                    arrow
                                                    enterTouchDelay={0}
                                                    leaveTouchDelay={6000}
                                                >
                                                    <InfoOutlined
                                                        sx={{
                                                            color: '#686868',
                                                            cursor: 'pointer',
                                                            fontSize: 20,
                                                        }}
                                                    />
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    },
                                }
                              : {}),
                      }
                    : undefined
            }
        >
            {isSelect
                ? options.map((opt, index) => (
                      <MenuItem key={index} value={opt.value as string | number}>
                          {opt.label}
                      </MenuItem>
                  ))
                : null}
        </TextField>
    );
};

export default FormField;
